import Papa, { type ParseError, type ParseResult } from "papaparse";

// Caps applied before shipping the sample to the LLM. Matches the contract we
// agreed on with the backend prompt: first 10 rows × first 100 columns.
export const CSV_MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
export const CSV_MAX_SAMPLED_ROWS = 10;
export const CSV_MAX_SAMPLED_COLS = 100;

export interface ParsedCsv {
    fileName: string;
    fileSizeBytes: number;
    rowCount: number;                  // total rows in the file (header detection is deferred)
    columnCount: number;               // total columns in the file
    sampledRowCount: number;           // ≤ CSV_MAX_SAMPLED_ROWS
    sampledColumnCount: number;        // ≤ CSV_MAX_SAMPLED_COLS
    // Raw sample as a 2D array of cells. The first row may or may not be a
    // header — the LLM decides based on the values.
    sampleRows: Array<Array<string | null>>;
    truncated: { rows: boolean; columns: boolean };
    parseErrors: string[];             // papaparse error messages
}

function coerceCell(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const s = typeof value === "string" ? value : String(value);
    const trimmed = s.trim();
    return trimmed === "" ? null : trimmed;
}

export async function parseCsvFile(file: File): Promise<ParsedCsv> {
    if (file.size > CSV_MAX_FILE_BYTES) {
        throw new Error(`File is larger than ${(CSV_MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB.`);
    }

    return new Promise<ParsedCsv>((resolve, reject) => {
        Papa.parse<string[]>(file, {
            header: false,
            skipEmptyLines: "greedy",
            // delimiter intentionally left blank — papaparse auto-detects "," vs ";" vs "\t" vs "|".
            complete: (result: ParseResult<string[]>) => {
                try {
                    const allRows = (result.data || []) as string[][];
                    const rowCount = allRows.length;
                    const columnCount = allRows.reduce((max, r) => Math.max(max, (r || []).length), 0);
                    const sampledRowCount = Math.min(rowCount, CSV_MAX_SAMPLED_ROWS);
                    const sampledColumnCount = Math.min(columnCount, CSV_MAX_SAMPLED_COLS);

                    const sampleRows: Array<Array<string | null>> = [];
                    for (let i = 0; i < sampledRowCount; i++) {
                        const row = allRows[i] || [];
                        const trimmed: Array<string | null> = [];
                        for (let c = 0; c < sampledColumnCount; c++) {
                            trimmed.push(coerceCell(row[c]));
                        }
                        sampleRows.push(trimmed);
                    }

                    resolve({
                        fileName: file.name,
                        fileSizeBytes: file.size,
                        rowCount,
                        columnCount,
                        sampledRowCount,
                        sampledColumnCount,
                        sampleRows,
                        truncated: {
                            rows: rowCount > CSV_MAX_SAMPLED_ROWS,
                            columns: columnCount > CSV_MAX_SAMPLED_COLS,
                        },
                        parseErrors: (result.errors || []).map((e: ParseError) => e.message),
                    });
                } catch (e: any) {
                    reject(e);
                }
            },
            error: (err: Error) => reject(err),
        });
    });
}
